import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
  composeRenderProps,
} from 'react-aria-components'
import { tv, type VariantProps } from 'tailwind-variants'
import { focusRing } from '@/lib/style'
import { cn } from '@/lib/utils'
import styles from './button.module.css'

export type Variants = VariantProps<typeof button>

// Unlike `text`, we don't export the `button` style as public API
// Button component comes with other important stylistic features such as corner decorations, brackets around link text.
export const button = tv({
  extend: focusRing,
  // --border-color is a CSS variable used by the corner decorations (see button.module.css)
  base: `justify-center items-center whitespace-nowrap rounded-sm
    font-nb-architekt text-sm font-bold px-3 py-2.5 cursor-pointer
    transition-colors disabled:pointer-events-none disabled:bg-bg-muted/50
    disabled:[--border-color:color-mix(in_oklab,var(--color-bg-muted)_50%,transparent)]`, // ugly CSS copy pasted from bg-muted/50, because we can't use the same Tailwind custom syntax for CSS variables
  variants: {
    variant: {
      primary: cn(styles.primary_button, 'bg-gold text-black [--border-color:var(--color-gold)]'),
      link: 'bg-transparent text-gold',
    },
    flex: {
      true: 'flex',
      false: 'inline-flex',
    },
  },
  defaultVariants: {
    variant: 'primary',
    flex: false,
  },
})

export interface ButtonProps extends AriaButtonProps, Variants {
  children?: React.ReactNode
}

export const ButtonContent = ({
  variant,
  children,
}: {
  variant: Variants['variant']
  children?: React.ReactNode
}) => (
  <>
    {/* Corner decoration top left + bottom right (primary button only) */}
    {variant === 'primary' && <span className={styles.top_left_bottom_right}></span>}
    {/* Bracket to the left of button content (link button only) */}
    {variant === 'link' ? '[' : ''}
    {/* Button content */}
    {children}
    {/* Bracket to the right of button content (link button only) */}
    {variant === 'link' ? ']' : ''}
    {/* Corner decoration top right + bottom left (primary button only) */}
    {variant === 'primary' && <span className={styles.top_right_bottom_left}></span>}
  </>
)

/**
 * A button styled according to our design, and with react-aria-components Button underneath for interactions.
 * Why? https://react-spectrum.adobe.com/blog/building-a-button-part-1.html
 *
 * Uses `onPress` instead of the usual `onClick`
 */
export function Button({
  className,
  variant = button.defaultVariants.variant,
  flex,
  children,
  ...props
}: ButtonProps) {
  return (
    <AriaButton
      {...props}
      className={composeRenderProps(className, (className, renderProps) =>
        button({
          ...renderProps,
          variant,
          flex,
          className,
        }),
      )}
    >
      <ButtonContent variant={variant}>{children}</ButtonContent>
    </AriaButton>
  )
}
